// RUN HISTORY + LEADERBOARD data (owner 2026-11-03): a compact record per finished run (never training / tutorial), the
// last 50 kept, plus permanent per-map bests. Pure + storage-injected (own localStorage key, try/catch) so it is unit-tested.
import { buildDamageRows } from './damageReport';

export const HISTORY_KEY = 'arena.runs';
export const HISTORY_CAP = 50;
export const TOP_N = 10;

export interface RunRecord {
  /** ISO date */ at: string;
  map: string; mapName: string;
  /** run length setting, seconds until the final boss */ runLen: number;
  mods: string[]; difficulty: string;
  victory: boolean; time: number; endless: number; level: number; kills: number; bosses: number; shards: number;
  /** best Surge form reached (0 = none) */ form: number;
  cause?: string;
  skills: { id: string; rank: number; evo: boolean }[];
  fusions: string[];
  traits: { id: string; rank: number }[];
  dmg: { id: string; total: number; share: number }[];
}
export interface MapBest { time: number; kills: number; level: number; endless: number; runs: number }
export interface HistorySave { v: 1; runs: RunRecord[]; bests: Record<string, MapBest> }

/** the slice of Sim the record reads (structural, so tests can pass a plain object) */
export interface SimLike {
  time: number; kills: number; endless: boolean; endlessTime: number; bossesDefeated: number; bestSurgeTier: number;
  player: { level: number };
  map: { id: string; name: string };
  opts: { bossTime: number };
  weapons: Map<string, number>; evolved: Set<string>; passives: Map<string, number>;
  fusion: { owned: Map<string, unknown> };
  dmgBySrc: Record<string, number>; dmgSince: Record<string, number>;
  shardPayout: number;
  /** optional: what killed the hero, when known */
  causeOfDeath?: string;
}

const r1 = (n: number) => Math.round(n * 10) / 10;

export function buildRunRecord(sim: SimLike, o: { victory: boolean; difficulty?: string; mods?: string[]; now?: Date }): RunRecord {
  const rows = buildDamageRows(sim.dmgBySrc, sim.dmgSince, sim.time).slice(0, 8);
  const rec: RunRecord = {
    at: (o.now ?? new Date()).toISOString(),
    map: sim.map.id, mapName: sim.map.name, runLen: sim.opts.bossTime,
    mods: o.mods ?? [], difficulty: o.difficulty ?? 'normal',
    victory: o.victory, time: Math.floor(sim.time), endless: sim.endless ? Math.floor(sim.endlessTime) : 0,
    level: sim.player.level, kills: sim.kills, bosses: sim.bossesDefeated, shards: sim.shardPayout, form: sim.bestSurgeTier,
    skills: [...sim.weapons].map(([id, rank]) => ({ id, rank, evo: sim.evolved.has(id) })),
    fusions: [...sim.fusion.owned.keys()],
    traits: [...sim.passives].map(([id, rank]) => ({ id, rank })),
    dmg: rows.map((r) => ({ id: r.id, total: Math.round(r.dmg), share: r1(r.share) })),
  };
  if (sim.causeOfDeath) rec.cause = sim.causeOfDeath;
  return rec;
}

export const emptyHistory = (): HistorySave => ({ v: 1, runs: [], bests: {} });

/** better run first: longer time survived (endless time is already inside it), then more kills */
export const byScore = (a: RunRecord, b: RunRecord) => b.time - a.time || b.kills - a.kills;
export const topRuns = (runs: RunRecord[], map: string, n = TOP_N): RunRecord[] => runs.filter((r) => r.map === map).sort(byScore).slice(0, n);

/** add a run: newest last, oldest dropped past the cap; per-map bests are permanent (they survive the cap) */
export function addRun(h: HistorySave, rec: RunRecord): HistorySave {
  const runs = [...h.runs, rec];
  if (runs.length > HISTORY_CAP) runs.splice(0, runs.length - HISTORY_CAP);
  const b = h.bests[rec.map] ?? { time: 0, kills: 0, level: 0, endless: 0, runs: 0 };
  const bests = { ...h.bests, [rec.map]: { time: Math.max(b.time, rec.time), kills: Math.max(b.kills, rec.kills), level: Math.max(b.level, rec.level), endless: Math.max(b.endless, rec.endless), runs: b.runs + 1 } };
  return { v: 1, runs, bests };
}

/** tolerant loader: junk / missing -> empty */
export function parseHistory(s: string | null): HistorySave {
  try {
    const o = s ? JSON.parse(s) : null;
    if (!o || !Array.isArray(o.runs)) return emptyHistory();
    const runs = (o.runs as RunRecord[]).filter((r) => r && typeof r.map === 'string' && typeof r.time === 'number').slice(-HISTORY_CAP);
    const bests = o.bests && typeof o.bests === 'object' ? o.bests as Record<string, MapBest> : {};
    return { v: 1, runs, bests };
  } catch { return emptyHistory(); }
}
export function loadHistory(): HistorySave { try { return parseHistory(localStorage.getItem(HISTORY_KEY)); } catch { return emptyHistory(); } }
export function saveHistory(h: HistorySave): void { try { localStorage.setItem(HISTORY_KEY, JSON.stringify(h)); } catch { /* storage full / blocked */ } }
/** record a finished run and persist it */
export function logRun(sim: SimLike, o: { victory: boolean; difficulty?: string; mods?: string[] }): RunRecord {
  const rec = buildRunRecord(sim, o); saveHistory(addRun(loadHistory(), rec)); return rec;
}

export const exportName = (d = new Date()) => `shatterlight-runs-${d.toISOString().slice(0, 10).replace(/-/g, '')}.json`;
export const exportJson = (runs: RunRecord[], bests?: Record<string, MapBest>) => JSON.stringify({ game: 'shatterlight-arena', exported: new Date().toISOString(), runs, bests }, null, 2);

/** copy to the clipboard (textarea fallback) AND offer a file download; resolves true when the copy worked */
export async function exportOut(json: string, name = exportName()): Promise<boolean> {
  let copied = false;
  try { await navigator.clipboard.writeText(json); copied = true; } catch {
    try {
      const ta = document.createElement('textarea'); ta.value = json; ta.style.cssText = 'position:fixed;left:-999px;top:0';
      document.body.appendChild(ta); ta.select(); copied = document.execCommand('copy'); ta.remove();
    } catch { /* no clipboard */ }
  }
  try {
    const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
    const a = document.createElement('a'); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch { /* download blocked */ }
  return copied;
}
